import { useEffect, useState, useSyncExternalStore } from "react";
import { useCapture } from "../lib/captureContext";
import { fetchOffers, type OffersDto } from "../lib/offersPage";

let revision = 0;
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
const snapshot = () => revision;

/** Notify every mounted offer surface after a settled write, even partial failure. */
export function invalidateOffers() {
  revision += 1;
  listeners.forEach(listener => listener());
}
export function useOfferRevision() { return useSyncExternalStore(subscribe, snapshot, snapshot); }

// Locks outlive a mounted row: navigating to another offer surface must not
// start an overlapping write. Immutable snapshots notify every mounted view.
let busyOfferIds: ReadonlySet<number> = new Set();
const busyListeners = new Set<() => void>();
const subscribeBusy = (listener: () => void) => { busyListeners.add(listener); return () => { busyListeners.delete(listener); }; };
const busySnapshot = () => busyOfferIds;
function publishBusy(next: ReadonlySet<number>) {
  busyOfferIds = next;
  busyListeners.forEach(listener => listener());
}

export function useOfferMutations() {
  const busy = useSyncExternalStore(subscribeBusy, busySnapshot, busySnapshot);
  return { isBusy: (id: number) => busy.has(id), run: runOfferMutation };
}

/** Acquire every affected ID synchronously BEFORE invoking the writer. */
async function runOfferMutation(ids: readonly number[], operation: () => Promise<unknown>): Promise<void> {
  const affected = [...new Set(ids)];
  if (affected.length === 0 || affected.some(id => busyOfferIds.has(id))) return;
  publishBusy(new Set([...busyOfferIds, ...affected]));
  try {
    await operation();
  } finally {
    // Even a partially successful grouped write needs every surface reloaded.
    invalidateOffers();
    const next = new Set(busyOfferIds);
    affected.forEach(id => next.delete(id));
    publishBusy(next);
  }
}

export function useOfferData() {
  const [data, setData] = useState<OffersDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const revision = useOfferRevision();
  const { finished } = useCapture();
  useEffect(() => {
    let active = true;
    fetchOffers({ status: "All" }).then(value => {
      if (active) { setData(value); setError(null); }
    }, (error: unknown) => {
      if (active) setError(error instanceof Error ? error.message : String(error));
    });
    return () => { active = false; };
  }, [revision, finished]);
  return { data, error, setError, revision, finished };
}
